import { getTxCategory, txAmountParts } from "./dashboardHelpers";

/**
 * Client-side wallet analytics computed from a recent-transactions sample.
 * All money values are normalized to cash-equivalent (10 COIN = 1 CASH)
 * so mixed-currency activity can live on one axis.
 */

export const COIN_RATE = 10;

export const cashEquivalent = (tx = {}) => {
  const amount = Math.abs(Number(tx.amount) || 0);
  return String(tx.currency || "").toUpperCase() === "COIN"
    ? amount / COIN_RATE
    : amount;
};

/** Signed cash-equivalent flow. Returns 0 for pending/failed transactions. */
export const signedCashFlow = (tx = {}) => {
  const { signed, neutral } = txAmountParts(tx);
  if (neutral || signed === 0) return 0;
  const magnitude = cashEquivalent(tx);
  return signed > 0 ? magnitude : -magnitude;
};

const localDayKey = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const dayKeyToDate = (key) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ""));
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

export const dayKeyLabel = (key) => {
  const dt = dayKeyToDate(key);
  if (!dt) return String(key || "");
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * Per-day money flow series ending today.
 * `days` is a number or "all" (clamped to the sample's oldest transaction).
 * Each point: { date, credits, debits, net, count, cumulativeNet }.
 */
export const buildDailyFlowSeries = (transactions = [], days = 30) => {
  const settled = transactions.filter((tx) => tx?.createdAt);
  if (!settled.length) return [];

  const byDay = new Map();
  let oldest = null;
  settled.forEach((tx) => {
    const key = localDayKey(tx.createdAt);
    if (!key) return;
    const dt = dayKeyToDate(key);
    if (!oldest || dt < oldest) oldest = dt;
    let bucket = byDay.get(key);
    if (!bucket) {
      bucket = { credits: 0, debits: 0, count: 0 };
      byDay.set(key, bucket);
    }
    bucket.count += 1;
    const flow = signedCashFlow(tx);
    if (flow > 0) bucket.credits += flow;
    else if (flow < 0) bucket.debits += Math.abs(flow);
  });

  if (!oldest) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let start;
  if (days === "all") {
    start = oldest;
  } else {
    start = new Date(today);
    start.setDate(start.getDate() - (Number(days) - 1));
    if (start < oldest) start = oldest;
  }

  const series = [];
  let cumulativeNet = 0;
  const cursor = new Date(start);
  while (cursor <= today) {
    const key = localDayKey(cursor);
    const bucket = byDay.get(key) || { credits: 0, debits: 0, count: 0 };
    const net = bucket.credits - bucket.debits;
    cumulativeNet += net;
    series.push({
      date: key,
      credits: bucket.credits,
      debits: bucket.debits,
      net,
      count: bucket.count,
      cumulativeNet,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return series;
};

export const summarizeFlowSeries = (series = []) => {
  let credits = 0;
  let debits = 0;
  let count = 0;
  let best = null;
  let worst = null;
  series.forEach((p) => {
    credits += p.credits;
    debits += p.debits;
    count += p.count;
    if (p.net > 0 && (!best || p.net > best.net)) best = p;
    if (p.net < 0 && (!worst || p.net < worst.net)) worst = p;
  });
  return { credits, debits, net: credits - debits, count, best, worst };
};

export const FAMILY_LABELS = {
  credit: "Top-ups & rewards",
  debit: "Withdrawals & fees",
  betting: "P2P stakes",
  win: "Wins",
  loss: "Losses",
  cancel: "Refunds & cancels",
  transfer: "Conversions",
  tip: "Tips",
  default: "Other",
};

export const FAMILY_COLORS = {
  credit: "#2ecc71",
  win: "#46c479",
  debit: "#ef5350",
  loss: "#e57373",
  betting: "#1e88e5",
  transfer: "#64b5f6",
  cancel: "#90a4ae",
  tip: "#f06292",
  default: "#78909c",
};

/**
 * getTxCategory covers launch wallet, P2P, and prediction types.
 * Retired product rows stay in Other rather than looking like live P2P play.
 */
export const resolveTxFamily = (tx = {}) => {
  const { family } = getTxCategory(tx.transactionType);
  if (family !== "default") return family;
  const type = String(tx.transactionType || "").toLowerCase();
  if (/refund|cancel|reversal/.test(type)) return "cancel";
  if (/fee/.test(type)) return "debit";
  return "default";
};

/**
 * Where the money moves, grouped by activity family.
 * Rows sorted by absolute volume: { family, label, color, count, volume, inflow, outflow, net }.
 */
export const buildCategoryMix = (transactions = []) => {
  const map = new Map();
  transactions.forEach((tx) => {
    const flow = signedCashFlow(tx);
    const family = resolveTxFamily(tx);
    let row = map.get(family);
    if (!row) {
      row = {
        family,
        label: FAMILY_LABELS[family] || FAMILY_LABELS.default,
        color: FAMILY_COLORS[family] || FAMILY_COLORS.default,
        count: 0,
        volume: 0,
        inflow: 0,
        outflow: 0,
      };
      map.set(family, row);
    }
    row.count += 1;
    row.volume += Math.abs(flow);
    if (flow > 0) row.inflow += flow;
    else if (flow < 0) row.outflow += Math.abs(flow);
  });

  return [...map.values()]
    .map((row) => ({ ...row, net: row.inflow - row.outflow }))
    .sort((a, b) => b.volume - a.volume);
};

/** Headline facts about the sample: extremes, streaks, reliability. */
export const buildWalletInsights = (transactions = []) => {
  let biggestIn = null;
  let biggestOut = null;
  let winCount = 0;
  let winTotal = 0;
  let lossCount = 0;
  let successCount = 0;
  let pendingCount = 0;
  let failedCount = 0;
  const dayCounts = new Map();

  transactions.forEach((tx) => {
    const status = String(tx.status || "").toLowerCase();
    if (["pending", "hold", "onhold"].includes(status)) pendingCount += 1;
    else if (["failed", "cancelled", "canceled"].includes(status)) failedCount += 1;
    else successCount += 1;

    const key = localDayKey(tx.createdAt);
    if (key) dayCounts.set(key, (dayCounts.get(key) || 0) + 1);

    const flow = signedCashFlow(tx);
    if (flow > 0 && (!biggestIn || flow > biggestIn.flow)) biggestIn = { tx, flow };
    if (flow < 0 && (!biggestOut || flow < biggestOut.flow)) biggestOut = { tx, flow };

    const family = resolveTxFamily(tx);
    if (family === "win") {
      winCount += 1;
      if (flow > 0) winTotal += flow;
    } else if (family === "loss") lossCount += 1;
  });

  let busiestDay = null;
  dayCounts.forEach((count, date) => {
    if (!busiestDay || count > busiestDay.count) busiestDay = { date, count };
  });

  const decided = winCount + lossCount;
  return {
    biggestIn,
    biggestOut,
    busiestDay,
    winCount,
    winTotal,
    lossCount,
    winRate: decided > 0 ? Math.round((winCount / decided) * 100) : null,
    successCount,
    pendingCount,
    failedCount,
    successRate: transactions.length
      ? Math.round((successCount / transactions.length) * 100)
      : null,
  };
};

/** Activity count by hour of day (24 buckets). */
export const buildHourHistogram = (transactions = []) => {
  const buckets = Array.from({ length: 24 }, () => 0);
  transactions.forEach((tx) => {
    if (!tx?.createdAt) return;
    const d = new Date(tx.createdAt);
    if (Number.isNaN(d.getTime())) return;
    buckets[d.getHours()] += 1;
  });
  const max = Math.max(...buckets, 1);
  let peakHour = null;
  buckets.forEach((count, hour) => {
    if (count > 0 && (peakHour == null || count > buckets[peakHour])) peakHour = hour;
  });
  return { buckets, max, peakHour };
};

/** COIN-side stats in coin units (earned vs spent) from the sample. */
export const buildCoinStats = (transactions = []) => {
  let earned = 0;
  let spent = 0;
  let count = 0;
  transactions.forEach((tx) => {
    if (String(tx.currency || "").toUpperCase() !== "COIN") return;
    const { signed, neutral } = txAmountParts(tx);
    if (neutral) return;
    count += 1;
    if (signed > 0) earned += signed;
    else spent += Math.abs(signed);
  });
  return { earned, spent, net: earned - spent, count };
};

export const compactMoney = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
